import { useEffect, useState } from "react";
import { BellRing, LogOut, KeyRound, Languages } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { SERVICE } from "@/lib/util";
import { enablePush, isSubscribed, isIOS, isStandalone } from "@/lib/push";
import { BackBar, Row, VehNo } from "@/components/common";
import Modal from "@/components/Modal";
import BiometricCard from "@/components/BiometricCard";
import PasswordInput from "@/components/PasswordInput";
import { tr as t, LangPicker } from "@/lib/i18n";

function PushCard() {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { isSubscribed().then(setOn); }, []);
  const enable = async () => {
    setBusy(true);
    try { await enablePush(); setOn(true); toast.success(t("تم تفعيل التنبيهات على هذا الجهاز")); } catch (e) { toast.error(e.message ? t(e.message) : errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="card p-4 flex items-center gap-3" data-testid="push-card">
      <BellRing className={`w-8 h-8 shrink-0 ${on ? "text-green-600" : "text-red-600"}`} />
      <div className="flex-1">
        <b>{t("تنبيهات الهاتف")}</b>
        <div className="text-sm text-slate-500">{on ? t("مفعّلة على هذا الجهاز") : isIOS() && !isStandalone() ? t("أضف التطبيق للشاشة الرئيسية أولاً") : t("غير مفعّلة")}</div>
      </div>
      {on ? <button data-testid="push-test-btn" className="btn btn-sm btn-ghost" onClick={() => api.post("/push/test").then((r) => toast(r.data.status === "sent" ? t("تم إرسال تنبيه تجريبي") : t("تعذر الإرسال لهذا الجهاز")))}>{t("تجربة")}</button>
        : <button data-testid="push-enable-btn" disabled={busy} onClick={enable} className="btn btn-md btn-red">{t("تفعيل")}</button>}
    </div>
  );
}

function PasswordModal({ open, onClose }) {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const save = async () => {
    try { await api.post("/auth/change-password", f); toast.success(t("تم تغيير كلمة المرور")); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Modal open={open} onClose={onClose} title={t("تغيير كلمة المرور")} testid="password-modal">
      <PasswordInput testid="current-password" placeholder={t("كلمة المرور الحالية")} value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} />
      <PasswordInput testid="new-password" placeholder={t("كلمة المرور الجديدة")} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} />
      <button data-testid="save-password-btn" onClick={save} className="btn btn-lg btn-dark w-full">{t("حفظ")}</button>
    </Modal>
  );
}

export default function MyAccount() {
  const { user, logout } = useAuth();
  const d = user.driver || {};
  const [pw, setPw] = useState(false);
  return (
    <div>
      <BackBar title={t("حسابي")} to="/driver" />
      <div className="p-4 space-y-4">
        <div className="card p-4 space-y-3" data-testid="lang-card">
          <b className="flex items-center gap-2"><Languages className="w-6 h-6 text-brand" />{t("اللغة")}{t("اللغة") !== "Language" && " / Language"}</b>
          <LangPicker />
        </div>
        <div className="card p-5">
          <Row label={t("الاسم")}>{d.full_name}</Row>
          <Row label={t("الرقم التسلسلي")}><span className="ltr">{d.serial}</span></Row>
          <Row label={t("الهاتف")}><span className="ltr">{d.phone}</span></Row>
          <Row label={t("نوع العمل")}>{(d.work_types || []).map((w) => t(SERVICE[w])).join(" + ")}</Row>
          <Row label={t("المركبة")}><VehNo adm={d.vehicle?.admin_number} plate={d.vehicle?.plate} /></Row>
        </div>
        <PushCard />
        <BiometricCard loginId={user.phone || d.phone} />
        <button data-testid="change-password-btn" onClick={() => setPw(true)} className="btn btn-lg btn-ghost w-full"><KeyRound className="w-6 h-6" />{t("تغيير كلمة المرور")}</button>
        <button data-testid="logout-btn" onClick={logout} className="btn btn-lg btn-ghost w-full text-red-600"><LogOut className="w-6 h-6" />{t("تسجيل الخروج")}</button>
      </div>
      <PasswordModal open={pw} onClose={() => setPw(false)} />
    </div>
  );
}
